import { useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import {
  adminListDrinks,
  adminGetVersion,
  adminCreateDrink,
  adminDeleteDrink,
  adminUpdateDraft,
  listPrepRecipes,
  adminGetPrep,
  adminCreatePrep,
  adminDeletePrep,
  adminUpdatePrep,
} from "../../lib/api";

// ── CSV utilities ────────────────────────────────────────────────────────────

function csvEsc(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function toCsvRow(cells: (string | number | null | undefined)[]): string {
  return cells.map(csvEsc).join(",");
}

function downloadCsv(filename: string, rows: string[]): void {
  const blob = new Blob([rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsv(raw: string): string[][] {
  const rows: string[][] = [];
  let pos = 0;
  const n = raw.length;
  while (pos < n) {
    const row: string[] = [];
    while (true) {
      let cell = "";
      if (pos < n && raw[pos] === '"') {
        pos++;
        while (pos < n) {
          if (raw[pos] === '"') {
            pos++;
            if (pos < n && raw[pos] === '"') { cell += '"'; pos++; }
            else break;
          } else {
            cell += raw[pos++];
          }
        }
      } else {
        while (pos < n && raw[pos] !== "," && raw[pos] !== "\n" && raw[pos] !== "\r") {
          cell += raw[pos++];
        }
        cell = cell.trim();
      }
      row.push(cell);
      if (pos < n && raw[pos] === ",") { pos++; continue; }
      break;
    }
    if (pos < n && raw[pos] === "\r") pos++;
    if (pos < n && raw[pos] === "\n") pos++;
    if (row.length > 1 || row[0] !== "") rows.push(row);
  }
  return rows;
}

// ── Column definitions ───────────────────────────────────────────────────────

const PREP_COLS = [
  "name", "category", "service_status", "yield_amount", "yield_unit",
  "shelf_life_days", "storage", "labeling_instructions",
  "ingredients_text", "build_instructions", "staff_notes", "warning",
];

const COCKTAIL_COLS = [
  "name", "category", "service_status", "glass", "ice",
  "method", "garnish", "warning", "instructions", "staff_notes",
];

type ImportType = "preps" | "cocktails";

interface ImportResults {
  created: string[];
  skipped: string[];
  errors: Array<{ name: string; error: string }>;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function AdminImportExport() {
  const [exportBusy, setExportBusy] = useState<ImportType | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const [importType, setImportType] = useState<ImportType>("preps");
  const [importPreview, setImportPreview] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // ── Export ──────────────────────────────────────────────────────────────────

  async function exportPreps() {
    setExportBusy("preps");
    setExportError(null);
    try {
      const { preps } = await listPrepRecipes("all");
      const details = await Promise.all(preps.map(p => adminGetPrep({ slug: p.slug })));
      const today = new Date().toISOString().slice(0, 10);
      const rows = [
        toCsvRow(PREP_COLS),
        ...details.map(d => {
          const v = d.version;
          return toCsvRow([
            d.prep.name, d.prep.category, d.prep.service_status,
            v?.yield_amount ?? "", v?.yield_unit ?? "", v?.shelf_life_days ?? "",
            v?.storage ?? "", v?.labeling_instructions ?? "",
            v?.ingredients_text ?? "", v?.build_instructions ?? "",
            v?.staff_notes ?? "", v?.warning ?? "",
          ]);
        }),
      ];
      downloadCsv(`prep-recipes-${today}.csv`, rows);
    } catch (e: any) {
      setExportError(e?.message ?? "Export failed");
    } finally {
      setExportBusy(null);
    }
  }

  async function exportCocktails() {
    setExportBusy("cocktails");
    setExportError(null);
    try {
      const { drinks } = await adminListDrinks("all");
      const withVersion = drinks.filter(d => d.display_version_id);
      const versionData = await Promise.all(
        withVersion.map(d => adminGetVersion(d.display_version_id!))
      );
      const today = new Date().toISOString().slice(0, 10);
      const rows = [
        toCsvRow(COCKTAIL_COLS),
        ...withVersion.map((d, i) => {
          const v = versionData[i].version;
          return toCsvRow([
            d.name, d.category, d.service_status,
            v.glass ?? "", v.ice ?? "", v.method ?? "", v.garnish ?? "", v.warning ?? "",
            (v.instructions ?? []).join("\n"), v.staff_notes ?? "",
          ]);
        }),
      ];
      downloadCsv(`cocktail-recipes-${today}.csv`, rows);
    } catch (e: any) {
      setExportError(e?.message ?? "Export failed");
    } finally {
      setExportBusy(null);
    }
  }

  // ── Import ──────────────────────────────────────────────────────────────────

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    setImportResults(null);
    setImportError(null);
    if (!file) { setImportPreview(null); return; }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result as string;
      const parsed = parseCsv(text);
      if (parsed.length < 2) {
        setImportPreview(null);
        setImportError("CSV is empty or has no data rows.");
        return;
      }
      setImportPreview({ headers: parsed[0], rows: parsed.slice(1) });
    };
    reader.readAsText(file);
  }

  async function runImport() {
    if (!importPreview || importBusy) return;
    setImportBusy(true);
    setImportResults(null);
    setImportError(null);

    const { headers, rows } = importPreview;
    const col = (name: string) => headers.indexOf(name);

    if (col("name") === -1) {
      setImportError('CSV must have a "name" header column.');
      setImportBusy(false);
      return;
    }

    const created: string[] = [];
    const skipped: string[] = [];
    const errors: Array<{ name: string; error: string }> = [];

    const rowsToImport = rows.filter((r) => r[col("name")]?.trim());
    setImportProgress({ done: 0, total: rowsToImport.length });

    try {
      if (importType === "preps") {
        const { preps: existing } = await listPrepRecipes("all");
        const existingNames = new Set(existing.map(p => p.name.toLowerCase()));

        for (const row of rowsToImport) {
          const name = row[col("name")]?.trim()!;
          if (existingNames.has(name.toLowerCase())) {
            skipped.push(name);
            setImportProgress((p) => p && { ...p, done: p.done + 1 });
            continue;
          }

          const category = row[col("category")]?.trim() || "syrup";
          const svcStatus = row[col("service_status")]?.trim() || "Active";

          let prepId: string | null = null;
          try {
            const created_resp = await adminCreatePrep(name, category as any);
            prepId = created_resp.prep_id;
            const version_id = created_resp.version_id;
            const vf: Record<string, any> = {};
            for (const f of ["yield_unit", "storage", "labeling_instructions", "ingredients_text", "build_instructions", "staff_notes", "warning"]) {
              const val = col(f) !== -1 ? row[col(f)]?.trim() : undefined;
              if (val) vf[f] = val;
            }
            for (const f of ["yield_amount", "shelf_life_days"]) {
              const val = col(f) !== -1 ? row[col(f)]?.trim() : undefined;
              if (val) { const n = Number(val); if (!isNaN(n)) vf[f] = n; }
            }
            await adminUpdatePrep({
              version_id,
              prep: svcStatus !== "Active" ? { service_status: svcStatus as any } : undefined,
              version: Object.keys(vf).length ? vf : undefined,
            });
            created.push(name);
            existingNames.add(name.toLowerCase());
          } catch (e: any) {
            // Roll back the half-imported stub so a re-run doesn't see it
            // as "already exists" and skip the row permanently.
            if (prepId) {
              try { await adminDeletePrep(prepId); } catch { /* best-effort */ }
            }
            errors.push({ name, error: e?.message ?? "Unknown error" });
          }
          setImportProgress((p) => p && { ...p, done: p.done + 1 });
        }
      } else {
        const { drinks: existing } = await adminListDrinks("all");
        const existingNames = new Set(existing.map(d => d.name.toLowerCase()));

        for (const row of rowsToImport) {
          const name = row[col("name")]?.trim()!;
          if (existingNames.has(name.toLowerCase())) {
            skipped.push(name);
            setImportProgress((p) => p && { ...p, done: p.done + 1 });
            continue;
          }

          const category = row[col("category")]?.trim() || "Whiskey";
          const svcStatus = row[col("service_status")]?.trim() || "Active";

          let drinkId: string | null = null;
          try {
            const created_resp = await adminCreateDrink(name, category);
            drinkId = created_resp.drink_id;
            const version_id = created_resp.version_id;
            const vf: Record<string, any> = {};
            for (const f of ["glass", "ice", "method", "garnish", "warning", "staff_notes"]) {
              const val = col(f) !== -1 ? row[col(f)]?.trim() : undefined;
              if (val) vf[f] = val;
            }
            if (col("instructions") !== -1 && row[col("instructions")]?.trim()) {
              vf.instructions = row[col("instructions")].split("\n").map((s: string) => s.trim()).filter(Boolean);
            }
            await adminUpdateDraft({
              version_id,
              drink: svcStatus !== "Active" ? { service_status: svcStatus } : undefined,
              version: Object.keys(vf).length ? vf : undefined,
            });
            created.push(name);
            existingNames.add(name.toLowerCase());
          } catch (e: any) {
            // Roll back the half-imported stub so a re-run doesn't see it
            // as "already exists" and skip the row permanently.
            if (drinkId) {
              try { await adminDeleteDrink(drinkId); } catch { /* best-effort */ }
            }
            errors.push({ name, error: e?.message ?? "Unknown error" });
          }
          setImportProgress((p) => p && { ...p, done: p.done + 1 });
        }
      }
    } catch (e: any) {
      setImportError(e?.message ?? "Import failed");
    }

    setImportResults({ created, skipped, errors });
    setImportProgress(null);
    setImportBusy(false);
  }

  const isBusyExporting = exportBusy !== null;

  return (
    <section style={styles.shell}>
      <div style={styles.headerRow}>
        <div>
          <p style={styles.eyebrow}>Admin</p>
          <h1 style={styles.title}>Import / Export</h1>
        </div>
      </div>

      {/* Export ───────────────────────────────────────────────────────────── */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Export</h2>
        <p style={styles.cardDesc}>
          Downloads all recipes as CSV. The active version is exported for each recipe.
          Cocktail ingredients are not included — manage them in the recipe editor.
        </p>
        {exportError && <div style={styles.errorBox}>{exportError}</div>}
        <div style={styles.btnRow}>
          <button
            type="button"
            onClick={exportPreps}
            disabled={isBusyExporting}
            style={styles.primaryBtn}
          >
            {exportBusy === "preps" ? "Exporting…" : "Export Prep Recipes (CSV)"}
          </button>
          <button
            type="button"
            onClick={exportCocktails}
            disabled={isBusyExporting}
            style={styles.primaryBtn}
          >
            {exportBusy === "cocktails" ? "Exporting…" : "Export Cocktail Recipes (CSV)"}
          </button>
        </div>
      </div>

      {/* Import ───────────────────────────────────────────────────────────── */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Import</h2>
        <p style={styles.cardDesc}>
          Creates new records only — rows whose name already exists are skipped.
          Cocktail ingredients must be added via the recipe editor after import.
        </p>

        <div style={styles.fieldGroup}>
          <div style={styles.field}>
            <label style={styles.fieldLabel} htmlFor="import-type">Recipe type</label>
            <select
              id="import-type"
              value={importType}
              onChange={(e) => {
                setImportType(e.target.value as ImportType);
                setImportPreview(null);
                setImportResults(null);
                setImportError(null);
              }}
              style={styles.select}
              disabled={importBusy}
            >
              <option value="preps">Prep Recipes</option>
              <option value="cocktails">Cocktail Recipes</option>
            </select>
          </div>
          <div style={styles.field}>
            <span style={styles.fieldLabel}>Expected columns</span>
            <p style={styles.colHint}>
              {(importType === "preps" ? PREP_COLS : COCKTAIL_COLS).join(", ")}
            </p>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.fieldLabel} htmlFor="import-file">CSV file</label>
          <input
            id="import-file"
            key={importType}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            style={styles.fileInput}
            disabled={importBusy}
          />
        </div>

        {importError && <div style={{ ...styles.errorBox, marginTop: 12 }}>{importError}</div>}

        {importPreview && (
          <div style={styles.previewBlock}>
            <p style={styles.previewMeta}>
              {importPreview.rows.length} row{importPreview.rows.length !== 1 ? "s" : ""}
              {" · "}columns: {importPreview.headers.join(", ")}
            </p>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {importPreview.headers.map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importPreview.rows.slice(0, 5).map((row, ri) => (
                    <tr key={`row-${ri}`}>
                      {importPreview.headers.map((h, ci) => (
                        <td key={h} style={styles.td}>{row[ci] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {importPreview.rows.length > 5 && (
              <p style={styles.previewMore}>…and {importPreview.rows.length - 5} more rows</p>
            )}
            <button
              type="button"
              onClick={runImport}
              disabled={importBusy}
              style={{ ...styles.primaryBtn, marginTop: 14 }}
            >
              {importBusy
                ? importProgress
                  ? `Importing ${importProgress.done}/${importProgress.total}…`
                  : "Importing…"
                : `Import ${importPreview.rows.length} row${importPreview.rows.length !== 1 ? "s" : ""}`}
            </button>
            {importBusy && importProgress && (
              <p role="status" aria-live="polite" style={{ ...styles.previewMore, marginTop: 6 }}>
                {importProgress.done} of {importProgress.total} rows processed
              </p>
            )}
          </div>
        )}

        {importResults && (
          <div style={styles.resultsBlock}>
            <div style={styles.badgeRow}>
              <span style={styles.badgeGreen}>{importResults.created.length} created</span>
              {importResults.skipped.length > 0 && (
                <span style={styles.badgeMuted}>{importResults.skipped.length} skipped</span>
              )}
              {importResults.errors.length > 0 && (
                <span style={styles.badgeRed}>{importResults.errors.length} failed</span>
              )}
            </div>
            {importResults.created.length > 0 && (
              <ul style={styles.resultList}>
                {importResults.created.map(n => (
                  <li key={n} style={styles.resultGreen}>✓ {n}</li>
                ))}
              </ul>
            )}
            {importResults.skipped.length > 0 && (
              <ul style={styles.resultList}>
                {importResults.skipped.map(n => (
                  <li key={n} style={styles.resultMuted}>— {n} (already exists)</li>
                ))}
              </ul>
            )}
            {importResults.errors.length > 0 && (
              <ul style={styles.resultList}>
                {importResults.errors.map(e => (
                  <li key={e.name} style={styles.resultRed}>✗ {e.name}: {e.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 900, padding: "8px 0 32px" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 24,
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 0",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 20,
    marginBottom: 16,
  },
  cardTitle: {
    margin: "0 0 6px",
    fontSize: 16,
    fontWeight: 800,
    color: "var(--text)",
  },
  cardDesc: {
    margin: "0 0 16px",
    fontSize: 13,
    color: "var(--muted)",
    lineHeight: 1.5,
  },
  btnRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  primaryBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  errorBox: {
    marginBottom: 12,
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  fieldGroup: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  select: {
    height: 40,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 10px",
    fontSize: 13,
    minWidth: 180,
  },
  colHint: {
    margin: 0,
    fontSize: 11,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    maxWidth: 480,
    lineHeight: 1.6,
  },
  fileInput: { fontSize: 13, color: "var(--text)" },
  previewBlock: {
    marginTop: 14,
    padding: 12,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
  },
  previewMeta: {
    margin: "0 0 8px",
    fontSize: 12,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "6px 10px",
    textAlign: "left",
    fontWeight: 800,
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "6px 10px",
    color: "var(--text)",
    borderBottom: "1px solid var(--border)",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  previewMore: { margin: "6px 0 0", fontSize: 11, color: "var(--muted)" },
  resultsBlock: {
    marginTop: 16,
    padding: 12,
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 12,
  },
  badgeRow: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  badgeGreen: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    background: "rgba(39,174,96,0.16)",
    color: "#86efac",
    border: "1px solid rgba(39,174,96,0.3)",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  badgeMuted: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    background: "var(--bg)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  badgeRed: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    background: "rgba(224,80,64,0.1)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.4)",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  resultList: { listStyle: "none", padding: 0, margin: "0 0 8px", display: "grid", gap: 3 },
  resultGreen: { fontSize: 12, color: "#86efac" },
  resultMuted: { fontSize: 12, color: "var(--muted)" },
  resultRed: { fontSize: 12, color: "#FF8A7A" },
};
